import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";

import { Channel } from "../../../domain/channel";
import { UserService } from "../../../domain/user-service";
import { Socket } from "../../../infrastructure/socket.service";
import { WebRTCPreview } from "app/js/src/domain/web-r-t-c-preview";
import { v4 as uuid } from "uuid";

@Component({
  selector: "icn-channel-preview",
  templateUrl: "channel-preview.component.html",
  styleUrls: ["channel-preview.component.scss"],
})
export class ChannelPreviewComponent implements OnInit, OnDestroy {
  isDragged = false;
  isShow = false;
  currentChannel: Channel;
  currentChannelPreview: WebRTCPreview;

  private lastPositionX: number;
  private lastPositionY: number;

  translateX = 0;
  translateY = 0;

  chosenQuality = "240p";
  showQualitySettings = false;
  connectionId: string;
  @ViewChild("fullscreenContainer") fullscreenContainer: ElementRef;
  @ViewChild("preview") preview: ElementRef;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private userService: UserService,
    private socket: Socket,
  ) {}

  ngOnInit() {
    this.userService.currentChannelIdUpdated.subscribe((channelId) =>
      this.setCurrentChannel(),
    );
    this.userService.currentChannelSaved.subscribe((channel) =>
      this.setCurrentChannel(channel),
    );
    this.setCurrentChannel();
    document.addEventListener(
      "webkitfullscreenchange",
      this.onFullscreenChange.bind(this),
    );
    this.connectionId = this.socket.connectionId;
  }

  ngOnDestroy() {
    document.removeEventListener(
      "webkitfullscreenchange",
      this.onFullscreenChange,
    );
  }

  onFullscreenChange() {
    if (!!this.currentChannel) {
      if (this.isFullScreen()) {
        this.changeQuality(this.currentChannel.previewUrls[0].clientId);
      } else {
        this.changeQuality("240p");
      }
    }
  }

  private setCurrentChannel(channel?: Channel) {
    this.currentChannel = null;
    this.currentChannelPreview = null;
    const channelPromise = channel
      ? Promise.resolve(channel)
      : this.userService.getCurrentChannel();

    channelPromise.then((currentChannel) => {
      if (!currentChannel) {
        return;
      }

      this.currentChannel = currentChannel;
      this.getChannelPreview();
      this.changeDetectorRef.markForCheck();
    });
  }

  getChannelPreview() {
    if (
      !!this.currentChannel &&
      this.currentChannel.isActive &&
      !this.currentChannelPreview
    ) {
      this.socket
        .createChannelWebRTCPreview(this.currentChannel)
        .then((preview: WebRTCPreview) => {
          this.currentChannelPreview = preview;
        });
    }
  }
  onRefreshChannelPreview() {
    this.currentChannelPreview = null;
    this.getChannelPreview();
  }

  isChosenQuality(quality: string) {
    return this.chosenQuality === quality;
  }

  changeQuality(quality: string) {
    this.chosenQuality = quality;
    this.changeDetectorRef.markForCheck();
  }

  getAvailableQualities() {
    return this.currentChannel.previewUrls.map((x) => x.id);
  }

  enterFullscreen() {
    this.fullscreenContainer.nativeElement.webkitRequestFullScreen();
    this.changeDetectorRef.markForCheck();
  }

  exitFullscreen() {
    (document as any).webkitExitFullscreen();
    this.changeDetectorRef.markForCheck();
  }

  isFullScreen() {
    return (document as any).webkitIsFullScreen;
  }

  togglePlay() {
    if (this.preview.nativeElement.paused) {
      this.preview.nativeElement.play();
    } else {
      this.preview.nativeElement.pause();
    }
    this.changeDetectorRef.markForCheck();
  }
  isPaused() {
    return this.preview.nativeElement.paused;
  }

  isMuted() {
    return this.currentChannel.isMuted;
  }

  toggleMute() {
    this.currentChannel.isMuted = !this.currentChannel.isMuted;
    this.changeDetectorRef.markForCheck();
  }

  onMouseDown(event) {
    this.isDragged = true;
    this.lastPositionX = event.clientX;
    this.lastPositionY = event.clientY;
    this.changeDetectorRef.markForCheck();
  }

  onMouseMove(event) {
    if (!this.isDragged) {
      return;
    }

    const diffX = event.clientX - this.lastPositionX;
    const diffY = event.clientY - this.lastPositionY;
    this.lastPositionX = event.clientX;
    this.lastPositionY = event.clientY;

    this.translateX += diffX;
    this.translateY += diffY;
    this.changeDetectorRef.markForCheck();
  }

  onMouseUp(event) {
    this.isDragged = false;
    this.changeDetectorRef.markForCheck();
  }

  show(channel: Channel) {
    this.isShow = true;
    this.currentChannel = channel;
    this.changeDetectorRef.markForCheck();
    this.getChannelPreview();
  }

  close() {
    this.isShow = false;
    this.currentChannelPreview = null;
    this.changeDetectorRef.markForCheck();
  }

  isPreviewOpen() {
    return this.isShow;
  }

  openInNewTab() {
    if (!!this.currentChannel) {
      this.socket
        .createChannelWebRTCPreview(this.currentChannel)
        .then((preview: WebRTCPreview) => {
          const id = uuid();
          const data = {
            availabilityZoneUrls: preview.availabilityZoneUrls,
            clientId: preview.clientId,
            brodcasterId: preview.brodcasterId,
            tcpOnlyWebRTC: preview.tcpOnlyWebRTC,
            name: this.currentChannel.name,
          };
          localStorage.setItem(`stream_data_${id}`, JSON.stringify(data));
          window.open(
            `/html/preview.html?id=${id}`,
            `window_${this.currentChannel.name}_channel_preview`,
            "width=407,height=300,toolbar=no, menubar=no, resizable=yes",
          );
          this.close();
        });
    }
  }

  getChannelPoster() {
    if (this.currentChannel && this.currentChannel.placeholderImage) {
      return this.currentChannel.placeholderImage;
    }
    return "/assets/stream-preview-loader.gif";
  }
}
